import React, { useMemo } from "react";
import {
  ActionIcon,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { SalonService } from "../../../types/context";
import {
  LegacySimpleDiscountTemplateParams,
  SimpleDiscountTemplateParams,
} from "../../../types/packages";
import { formatCurrency } from "../../../utils/cashDrawerUtils";

export interface DiscountItemRow {
  salonServiceId: string | null;
  quantity: number | string;
}

/**
 * Builder state for the "Simple or Bundled Discount" offer. The operator
 * enters the total bundle price the customer pays; the discount that goes to
 * the backend is derived at save time as `separateTotal − bundlePrice`.
 * Number fields hold `number | string` because Mantine NumberInput emits "" while typing.
 */
export interface CombinedDiscountState {
  items: DiscountItemRow[];
  bundlePrice: number | string;
}

const num = (value: number | string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

export function defaultCombinedDiscountState(): CombinedDiscountState {
  return {
    items: [{ salonServiceId: null, quantity: 1 }],
    bundlePrice: "",
  };
}

/**
 * Load builder state from stored params. Legacy `discount_type: "percent"` rows
 * are converted to their equivalent flat discount at current service prices;
 * the flat is then translated to the bundle price the operator now edits.
 * On save the builder always emits `discount_type: "flat"`.
 */
export function combinedDiscountStateFromParams(
  params: LegacySimpleDiscountTemplateParams,
  priceByServiceId: Record<string, number> = {},
): CombinedDiscountState {
  const items =
    params.items.length > 0
      ? params.items.map((item) => ({
          salonServiceId: item.salon_service_id,
          quantity: item.quantity,
        }))
      : [{ salonServiceId: null, quantity: 1 }];
  const separate = params.items.reduce(
    (sum, item) =>
      sum + item.quantity * (priceByServiceId[item.salon_service_id] ?? 0),
    0,
  );
  const flatDiscount =
    params.discount_type === "percent"
      ? round2((separate * params.discount_value) / 100)
      : params.discount_value;
  return { items, bundlePrice: round2(Math.max(0, separate - flatDiscount)) };
}

/**
 * Backend-shaped params, or null while the builder is incomplete/invalid.
 * Converts the operator-entered bundle price into a `discount_value` the
 * backend contract still stores. Bundle price greater than the item total is
 * treated as invalid (the schema disallows a negative discount).
 */
export function combinedDiscountStateToParams(
  state: CombinedDiscountState,
  priceByServiceId: Record<string, number>,
): SimpleDiscountTemplateParams | null {
  const items = state.items
    .filter((item) => item.salonServiceId && num(item.quantity) >= 0.01)
    .map((item) => ({
      salon_service_id: item.salonServiceId as string,
      quantity: num(item.quantity),
    }));
  const rawText = String(state.bundlePrice).trim();
  const value = num(state.bundlePrice);
  if (
    items.length === 0 ||
    rawText === "" ||
    !Number.isFinite(value) ||
    value < 0
  ) {
    return null;
  }
  const separate = items.reduce(
    (sum, item) =>
      sum + item.quantity * (priceByServiceId[item.salon_service_id] ?? 0),
    0,
  );
  if (value > separate) return null;
  return {
    items,
    discount_type: "flat",
    discount_value: round2(separate - value),
  };
}

interface CombinedDiscountBuilderProps {
  services: SalonService[];
  value: CombinedDiscountState;
  onChange: (next: CombinedDiscountState) => void;
}

export default function CombinedDiscountBuilder({
  services,
  value,
  onChange,
}: CombinedDiscountBuilderProps) {
  const serviceOptions = useMemo(
    () =>
      services.map((service) => ({
        value: service.id,
        label: `${service.name} (${formatCurrency(service.default_price ?? 0)})`,
      })),
    [services],
  );
  const priceByServiceId = useMemo(
    () =>
      Object.fromEntries(
        services.map((service) => [service.id, service.default_price ?? 0]),
      ),
    [services],
  );

  const rawText = String(value.bundlePrice).trim();
  const enteredValue = num(value.bundlePrice);
  const previewSeparate = value.items.reduce(
    (sum, item) =>
      sum +
      (item.salonServiceId
        ? num(item.quantity) * (priceByServiceId[item.salonServiceId] ?? 0)
        : 0),
    0,
  );
  const bundleTooHigh =
    rawText !== "" &&
    Number.isFinite(enteredValue) &&
    previewSeparate > 0 &&
    enteredValue > previewSeparate;

  const updateItem = (index: number, updates: Partial<DiscountItemRow>) =>
    onChange({
      ...value,
      items: value.items.map((item, i) =>
        i === index ? { ...item, ...updates } : item,
      ),
    });

  const addItem = () =>
    onChange({
      ...value,
      items: [...value.items, { salonServiceId: null, quantity: 1 }],
    });

  const removeItem = (index: number) =>
    onChange({
      ...value,
      items:
        value.items.length > 1
          ? value.items.filter((_, i) => i !== index)
          : value.items,
    });

  return (
    <Stack gap="md">
      <Stack gap="xs">
        {value.items.map((item, index) => (
          <Group key={index} align="flex-end" wrap="nowrap" gap="sm">
            <Select
              label={index === 0 ? "Service" : undefined}
              aria-label="Service"
              placeholder="Select a service"
              data={serviceOptions}
              value={item.salonServiceId}
              onChange={(next) => updateItem(index, { salonServiceId: next })}
              searchable
              style={{ flex: 1 }}
            />
            <NumberInput
              label={index === 0 ? "Quantity" : undefined}
              aria-label="Quantity"
              value={item.quantity}
              onChange={(next) => updateItem(index, { quantity: next })}
              min={0.01}
              decimalScale={2}
              hideControls
              allowNegative={false}
              w={110}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              size="lg"
              aria-label="Remove service"
              disabled={value.items.length <= 1}
              onClick={() => removeItem(index)}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Group>
        ))}
        <Group justify="flex-start">
          <Button
            variant="subtle"
            color="var(--brand-teal)"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={addItem}
          >
            Add service
          </Button>
        </Group>
      </Stack>

      <NumberInput
        label="Bundle Price"
        aria-label="Bundle Price"
        description="Total price the customer pays for the bundle."
        placeholder="0.00"
        leftSection="$"
        value={value.bundlePrice}
        onChange={(next) =>
          onChange({
            ...value,
            bundlePrice: typeof next === "number" ? next : "",
          })
        }
        min={0}
        decimalScale={2}
        allowNegative={false}
        hideControls
        error={
          bundleTooHigh
            ? `Bundle price cannot exceed the item total (${formatCurrency(
                previewSeparate,
              )}).`
            : undefined
        }
      />
    </Stack>
  );
}
